import React, { useState, useRef, useEffect } from 'react';
import { MoreHorizontal, BookmarkPlus, ThumbsUp, ThumbsDown } from 'lucide-react';

interface MessageActionsProps {
  message: {
    id: string;
    text: string;
    coachId?: string;
  };
  isBookmarked: boolean;
  isThirdMessage: boolean;
  hasSeenBookmarkTip: boolean;
  onBookmark: () => void;
  onFeedback?: (type: 'helpful' | 'not_helpful') => void;
}

const MessageActions: React.FC<MessageActionsProps> = ({
  message,
  isBookmarked,
  isThirdMessage,
  hasSeenBookmarkTip,
  onBookmark,
  onFeedback
}) => {
  const [showPopup, setShowPopup] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        setShowPopup(false);
      }
    };

    if (showPopup) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPopup]);

  const handleBookmarkClick = () => {
    onBookmark();
    setShowPopup(false);
  };

  const handleFeedbackClick = (type: 'helpful' | 'not_helpful') => {
    onFeedback?.(type);
    setShowPopup(false);
  };

  return (
    <div className="relative flex flex-col items-end gap-1">
      {/* Three-dots button */}
      <button
        onClick={() => setShowPopup(!showPopup)}
        className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1 rounded-full hover:bg-slate-100"
        aria-label="Message actions"
      >
        <MoreHorizontal className="h-4 w-4 text-slate-400 hover:text-slate-600" />
      </button>

      {/* Third message onboarding hint */}
      {isThirdMessage && !hasSeenBookmarkTip && (
        <div className="text-xs text-slate-500 animate-pulse">
          Like any suggestions? Bookmark it here
        </div>
      )}

      {/* Action popup menu */}
      {showPopup && (
        <div
          ref={popupRef}
          className="absolute top-8 right-0 bg-white rounded-lg shadow-lg border border-slate-200 py-1 min-w-[140px] z-50"
        >
          <button
            onClick={handleBookmarkClick}
            className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
          >
            <BookmarkPlus className={`h-4 w-4 ${isBookmarked ? 'text-blue-600 fill-blue-100' : 'text-slate-600'}`} />
            {isBookmarked ? 'Remove bookmark' : 'Bookmark'}
          </button>
          
          <button
            onClick={() => handleFeedbackClick('helpful')}
            className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
          >
            <ThumbsUp className="h-4 w-4 text-slate-600" />
            Helpful
          </button>
          
          <button
            onClick={() => handleFeedbackClick('not_helpful')}
            className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
          >
            <ThumbsDown className="h-4 w-4 text-slate-600" />
            Not helpful
          </button>
        </div>
      )}
    </div>
  );
};

export default MessageActions;
